import { baseApi } from './baseApi'

export type ExternalApiCredentialDto = {
  id: number
  companyId: number
  slug: string
  displayName: string
  isActive: boolean
  termsAcceptedAt: string | null
  termsAcceptedByUserId: number | null
  termsVersion: string | null
  createdAt: string
  updatedAt: string
}

export type ExternalApiToken = {
  id: number
  credentialId: number
  username: string
  accessToken: string
  refreshToken: string
  accessTokenExpiresAt: string
  createdAt: string
}

export type CreateExternalApiCredentialDto = {
  displayName: string
  // slug is auto-generated from displayName
}

export type CreateExternalApiCredentialResponse = ExternalApiCredentialDto & {
  clientId: string
  clientSecret: string
}

export type UpdateExternalApiCredentialDto = {
  slug?: string
  displayName?: string
  isActive?: boolean
}

export const externalApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getExternalApiCredential: builder.query<ExternalApiCredentialDto | null, number>({
      query: (companyId) => ({
        url: `/lab/companies/${companyId}/external-api`,
      }),
      providesTags: (result, error, companyId) => [
        { type: 'ExternalApiCredential', id: companyId },
      ],
    }),
    createExternalApiCredential: builder.mutation<
      CreateExternalApiCredentialResponse,
      { companyId: number; data: CreateExternalApiCredentialDto }
    >({
      query: ({ companyId, data }) => ({
        method: 'POST',
        url: `/lab/companies/${companyId}/external-api`,
        body: data,
      }),
      invalidatesTags: (result, error, { companyId }) => [
        { type: 'ExternalApiCredential', id: companyId },
      ],
    }),
    updateExternalApiCredential: builder.mutation<
      ExternalApiCredentialDto,
      { companyId: number; data: UpdateExternalApiCredentialDto }
    >({
      query: ({ companyId, data }) => ({
        method: 'PUT',
        url: `/lab/companies/${companyId}/external-api`,
        body: data,
      }),
      invalidatesTags: (result, error, { companyId }) => [
        { type: 'ExternalApiCredential', id: companyId },
      ],
    }),
    deactivateExternalApiCredential: builder.mutation<
      { message: string },
      number
    >({
      query: (companyId) => ({
        method: 'DELETE',
        url: `/lab/companies/${companyId}/external-api`,
      }),
      invalidatesTags: (result, error, companyId) => [
        { type: 'ExternalApiCredential', id: companyId },
      ],
    }),
    getExternalApiTokens: builder.query<ExternalApiToken[], number>({
      query: (companyId) => ({
        url: `/lab/companies/${companyId}/external-api/tokens`,
      }),
      providesTags: (result, error, companyId) => [
        { type: 'ExternalApiCredential', id: companyId },
      ],
    }),
    getTermsStatus: builder.query<TermsStatusResponse, void>({
      query: () => ({
        url: `/dme/external-api/terms/status`,
        method: 'GET',
      }),
      providesTags: ['ExternalApiCredential'],
    }),
    acceptTerms: builder.mutation<AcceptTermsResponse, { termsVersion: string }>({
      query: ({ termsVersion }) => ({
        url: `/dme/external-api/terms/accept`,
        method: 'POST',
        body: { termsVersion },
      }),
      invalidatesTags: ['ExternalApiCredential'],
    }),
  }),
})

export type TermsStatusResponse = {
  isAccepted: boolean
  acceptedAt: string | null
  acceptedByUserId: number | null
  termsVersion: string | null
  requiresAcceptance: boolean
}

export type AcceptTermsResponse = {
  acceptedAt: string
  acceptedBy: {
    userId: number
    firstName: string
    lastName: string
  }
  termsVersion: string
}

export const {
  useGetExternalApiCredentialQuery,
  useCreateExternalApiCredentialMutation,
  useUpdateExternalApiCredentialMutation,
  useDeactivateExternalApiCredentialMutation,
  useGetExternalApiTokensQuery,
  useGetTermsStatusQuery,
  useAcceptTermsMutation,
} = externalApiService
